/**
 * SKU A — Evidence & dual-agent door (verification-loop production-readiness).
 *
 * HTTP twins of the MCP `excellent-mcp evidence bundle|verify|export` surface
 * and the live-session core readers (`collectEvidenceBundleBody`,
 * `composeEvidenceBundle`, `verifyEvidenceBundleDocument`,
 * `measureSilentApproveRate`, `readTaskEvidenceArtifact`). Same vault rows the
 * CLI / MCP read — no parallel store.
 *
 * Dogfood surface: `/agents/verification` (excellent/client/routes/agent-verification.tsx).
 * Writes (accept/reject) stay on `review-decision.ts` → `override.human`.
 * `evidence.pack` is still GET, but the handler seals through the INV-8
 * export door (one `export.emitted` audit row) before files return.
 */

import { z } from "zod";
import { scoped, type Operation } from "../types";

// ── Shared DTOs (mirror of @excellent/core/live-sessions evidence-bundle) ────

const bundleClaimRow = z.object({
  seq: z.number().int(),
  actorId: z.string(),
  kind: z.string(),
  predicate: z.string(),
  fromSeq: z.number().int(),
  toSeq: z.number().int(),
  payloadHash: z.string(),
  prevHash: z.string(),
  chainHash: z.string(),
  /** VP-E1 pass-through when the claim payload carries falsifiers. */
  falsifiers: z.unknown().optional(),
});
export type EvidenceBundleClaimDto = z.infer<typeof bundleClaimRow>;

/** One row of `dod_judgment_receipts` — a dual-agent accept/reject verdict. */
const judgmentReceiptRow = z.object({
  verdict: z.string(),
  verifierAgent: z.string().nullable(),
  note: z.string().nullable(),
  createdAt: z.string(),
});
export type EvidenceJudgmentRow = z.infer<typeof judgmentReceiptRow>;

/** Wave 2 — derived residual-gap report (undischarged in-scope claims). */
const residualGapReason = z.enum([
  "no-oracle",
  "not-applicable",
  "oracle-inconclusive",
  "judged-and-failed",
]);
export type EvidenceResidualGapReason = z.infer<typeof residualGapReason>;

const residualGapRow = z.object({
  id: z.string(),
  predicate: z.string(),
  reason: residualGapReason,
  wouldDischarge: z.string(),
});
export type EvidenceResidualGapDto = z.infer<typeof residualGapRow>;

const residualGapReport = z.object({
  scopeStatus: z.enum(["known", "scope-unknown"]),
  /** null when scope cannot be enumerated — NEVER 0 in that case. */
  gapCount: z.number().int().nonnegative().nullable(),
  scopeCount: z.number().int().nonnegative().nullable(),
  dischargedCount: z.number().int().nonnegative(),
  gaps: z.array(residualGapRow),
  summary: z.string(),
});
export type EvidenceResidualGapReportDto = z.infer<typeof residualGapReport>;

const bundleReceiptRow = z.object({
  uid: z.string().nullable(),
  command: z.string(),
  exitCode: z.number().int(),
  treeHash: z.string(),
  binding: z.string().nullable(),
  envPolicy: z.string().nullable(),
  createdAt: z.string(),
  treeMatchesCommit: z.boolean().nullable(),
});
export type EvidenceBundleReceiptDto = z.infer<typeof bundleReceiptRow>;

const evidenceArtifactSummary = z
  .object({
    sessionUid: z.string().nullable(),
    seq: z.number().int(),
    verifier: z.string(),
    emittedAt: z.string(),
    bodyHash: z.string().nullable(),
    verdict: z.string().nullable(),
    claimCount: z.number().nullable(),
  })
  .nullable();

const bundleBody = z.object({
  task: z.object({
    id: z.number().int(),
    uid: z.string().nullable(),
    title: z.string(),
    status: z.string(),
  }),
  commit: z
    .object({
      sha: z.string(),
      treeHash: z.string().nullable(),
      linkKind: z.string().nullable(),
      linkedAt: z.string().nullable(),
    })
    .nullable(),
  gateVersion: z
    .object({
      checksManifestHash: z.string(),
      source: z.string(),
    })
    .nullable(),
  receipts: z.array(bundleReceiptRow),
  session: z
    .object({
      uid: z.string(),
      chainHead: z.string().nullable(),
    })
    .nullable(),
  claims: z.array(bundleClaimRow),
  aidRef: z.string().nullable(),
  evidenceArtifact: evidenceArtifactSummary,
  generator: z.string(),
});

const bundleSignature = z.union([
  z.object({ status: z.literal("unsigned"), reason: z.string() }),
  z.object({
    status: z.literal("signed"),
    algorithm: z.literal("ed25519"),
    publicKey: z.string(),
    signature: z.string(),
  }),
]);

const evidenceBundleDocument = z.object({
  format: z.string(),
  schemaVersion: z.number().int(),
  body: bundleBody,
  bundleHash: z.string(),
  signature: bundleSignature,
});
export type EvidenceBundleDocumentDto = z.infer<typeof evidenceBundleDocument>;

const verifyResult = z.union([
  z.object({
    ok: z.literal(true),
    checks: z.array(z.string()),
    signed: z.boolean(),
  }),
  z.object({
    ok: z.literal(false),
    mismatches: z.array(z.string()),
  }),
]);
export type EvidenceVerifyResultDto = z.infer<typeof verifyResult>;

const bundleTrust = z.object({
  status: z.enum(["signed", "unsigned"]),
  /** True when EXCELLENT_EVIDENCE_SIGNING_KEY was configured for this compose path. */
  signingConfigured: z.boolean(),
  /** Human-readable reason for unsigned or failed trust-root setup. */
  reason: z.string().nullable(),
  /** Raw Ed25519 public key, base64, when signed. */
  publicKey: z.string().nullable(),
  /** Offline verifier result projected into the trust badge. */
  verifyOk: z.boolean().nullable(),
  /** Compact operator-facing summary. */
  summary: z.string(),
});
export type EvidenceBundleTrustDto = z.infer<typeof bundleTrust>;

// ── List tasks with SKU A rollup ─────────────────────────────────────────────

const listQuery = z.object({
  /** review | done | all (default: review+done). */
  status: z.enum(["review", "done", "all"]).optional(),
  limit: z.coerce.number().int().positive().max(200).optional(),
});

const taskSummary = z.object({
  id: z.number().int().positive(),
  uid: z.string().nullable(),
  title: z.string(),
  status: z.string(),
  projectSlug: z.string(),
  sectionSlug: z.string(),
  projectName: z.string().nullable(),
  /** Historical producer provenance for imported task evidence. */
  ownerLabel: z.string().nullable(),
  /** Verifier (verified_by when done; else assignee awaiting). */
  verifierLabel: z.string().nullable(),
  consequenceTag: z.string().nullable(),
  claimCount: z.number().int().nonnegative(),
  receiptCount: z.number().int().nonnegative(),
  hasEvidenceArtifact: z.boolean(),
  hasBundle: z.boolean(),
  updatedAt: z.string().nullable(),
});
export type EvidenceTaskSummaryDto = z.infer<typeof taskSummary>;

const listResponse = z.object({
  tasks: z.array(taskSummary),
});
export type EvidenceTaskListDto = z.infer<typeof listResponse>;

const listOp: Operation<void, z.infer<typeof listQuery>, void, EvidenceTaskListDto> = {
  method: "GET",
  path: () => "/api/evidence/tasks",
  query: listQuery,
  response: listResponse,
};

// ── Verifier product console: templates + readiness + import ────────────────

const workflowTemplate = z.object({
  id: z.string(),
  title: z.string(),
  style: z.string(),
  objective: z.string(),
  expectedPredicates: z.array(z.string()),
  requirement: z.string(),
});
export type EvidenceWorkflowTemplateDto = z.infer<typeof workflowTemplate>;

const templatesResponse = z.object({
  templates: z.array(workflowTemplate),
});
export type EvidenceWorkflowTemplatesDto = z.infer<typeof templatesResponse>;

const templatesOp: Operation<void, void, void, EvidenceWorkflowTemplatesDto> = {
  method: "GET",
  path: () => "/api/evidence/templates",
  response: templatesResponse,
};

const readinessItem = z.object({
  id: z.string(),
  label: z.string(),
  status: z.enum(["ready", "warning", "blocked"]),
  detail: z.string(),
});
export type EvidenceReadinessItemDto = z.infer<typeof readinessItem>;

const readinessCompiler = z.object({
  status: z.enum(["ready", "warning", "blocked"]),
  model: z.string(),
  timeoutMs: z.number().int().positive(),
  detail: z.string(),
  checkedAt: z.string(),
});
export type EvidenceReadinessCompilerDto = z.infer<typeof readinessCompiler>;

const receiptLatencySummary = z.object({
  recentCount: z.number().int().nonnegative(),
  passing: z.number().int().nonnegative(),
  failing: z.number().int().nonnegative(),
  p95Ms: z.number().int().nonnegative().nullable(),
  lastMs: z.number().int().nonnegative().nullable(),
});
export type EvidenceReceiptLatencyDto = z.infer<typeof receiptLatencySummary>;

const readinessFailureRow = z.object({
  command: z.string(),
  tier: z.string().nullable(),
  exitCode: z.number().int(),
  createdAt: z.string(),
});
export type EvidenceReadinessFailureDto = z.infer<typeof readinessFailureRow>;

const readinessResponse = z.object({
  ok: z.boolean(),
  items: z.array(readinessItem),
  compiler: readinessCompiler,
  receiptLatency: receiptLatencySummary,
  recentFailures: z.array(readinessFailureRow),
  checkedAt: z.string(),
});
export type EvidenceReadinessDto = z.infer<typeof readinessResponse>;

const readinessOp: Operation<void, void, void, EvidenceReadinessDto> = {
  method: "GET",
  path: () => "/api/evidence/readiness",
  response: readinessResponse,
};

const workflowCandidateStatus = z.enum(["admit", "reject", "needs-oracle"]);
export type EvidenceWorkflowCandidateStatusDto = z.infer<typeof workflowCandidateStatus>;

const workflowCandidate = z.object({
  id: z.string(),
  name: z.string(),
  actor: z.string(),
  verifier: z.string(),
  oracle: z.string(),
  consequence: z.string(),
  evidence: z.string(),
  status: workflowCandidateStatus,
  rejectionReason: z.string().nullable(),
  updatedAt: z.string(),
});
export type EvidenceWorkflowCandidateDto = z.infer<typeof workflowCandidate>;

const workflowCandidatesResponse = z.object({
  candidates: z.array(workflowCandidate),
});
export type EvidenceWorkflowCandidatesDto = z.infer<typeof workflowCandidatesResponse>;

const workflowCandidatesOp: Operation<
  void,
  void,
  void,
  EvidenceWorkflowCandidatesDto
> = {
  method: "GET",
  path: () => "/api/evidence/workflow-candidates",
  response: workflowCandidatesResponse,
};

const workflowCandidateSaveBody = z.object({
  id: z.string().optional(),
  name: z.string().min(1).max(120),
  actor: z.string().min(1).max(160),
  verifier: z.string().min(1).max(160),
  oracle: z.string().min(1).max(240),
  consequence: z.string().min(1).max(240),
  evidence: z.string().min(1).max(240),
  status: workflowCandidateStatus,
  rejectionReason: z.string().max(240).optional(),
});
export type EvidenceWorkflowCandidateSaveBodyDto = z.infer<
  typeof workflowCandidateSaveBody
>;

const workflowCandidateSaveOp: Operation<
  void,
  void,
  EvidenceWorkflowCandidateSaveBodyDto,
  EvidenceWorkflowCandidatesDto
> = {
  method: "POST",
  path: () => "/api/evidence/workflow-candidates",
  body: workflowCandidateSaveBody,
  response: workflowCandidatesResponse,
  invalidates: [workflowCandidatesOp],
};

const workflowTemplateDraft = z.object({
  id: z.string(),
  candidateId: z.string(),
  title: z.string(),
  style: z.string(),
  objective: z.string(),
  actors: z.object({
    shipperRole: z.string(),
    verifierRole: z.string(),
  }),
  requirements: z.array(z.string()),
  externalOracles: z.array(z.string()),
  expectedPredicates: z.array(z.string()),
  requirement: z.string(),
  createdAt: z.string(),
});
export type EvidenceWorkflowTemplateDraftDto = z.infer<typeof workflowTemplateDraft>;

const workflowTemplateDraftBody = z.object({
  candidateId: z.string().min(1),
});
export type EvidenceWorkflowTemplateDraftBodyDto = z.infer<
  typeof workflowTemplateDraftBody
>;

const workflowTemplateDraftResponse = z.union([
  z.object({
    ok: z.literal(true),
    draft: workflowTemplateDraft,
  }),
  z.object({
    ok: z.literal(false),
    candidateId: z.string(),
    reason: z.string(),
  }),
]);
export type EvidenceWorkflowTemplateDraftResultDto = z.infer<
  typeof workflowTemplateDraftResponse
>;

const workflowTemplateDraftOp: Operation<
  void,
  void,
  EvidenceWorkflowTemplateDraftBodyDto,
  EvidenceWorkflowTemplateDraftResultDto
> = {
  method: "POST",
  path: () => "/api/evidence/workflow-candidates/template-draft",
  body: workflowTemplateDraftBody,
  response: workflowTemplateDraftResponse,
  invalidates: [workflowCandidatesOp, templatesOp],
};

// ── Get / download composed evidence bundle for a task ───────────────────────

const taskParams = z.object({
  id: z.coerce.number().int().positive(),
});

const bundleGetResponse = z.union([
  z.object({
    ok: z.literal(true),
    taskId: z.number().int().positive(),
	    document: evidenceBundleDocument,
	    trust: bundleTrust,
	    /** UTF-8 JSON of the document — client download target. */
	    downloadText: z.string(),
    byteLength: z.number().int().nonnegative(),
  }),
  z.object({
    ok: z.literal(false),
    taskId: z.number().int().positive(),
    reason: z.string(),
  }),
]);
export type EvidenceBundleGetDto = z.infer<typeof bundleGetResponse>;

const bundleGetOp: Operation<
  { id: number },
  void,
  void,
  EvidenceBundleGetDto
> = {
  method: "GET",
  path: ({ id }) => `/api/evidence/tasks/${id}/bundle`,
  params: taskParams,
  response: bundleGetResponse,
};

// ── Offline-verify (compose current vault state, then re-derive offline) ─────
// Pure read: no vault write. Same re-derive as `excellent-mcp evidence verify`.

const verifyResponse = z.object({
  taskId: z.number().int().positive(),
  source: z.literal("composed"),
	  result: verifyResult,
	  bundleHash: z.string().nullable(),
	  signatureStatus: z.string().nullable(),
	  trust: bundleTrust.nullable(),
	});
export type EvidenceVerifyDto = z.infer<typeof verifyResponse>;

const verifyOp: Operation<{ id: number }, void, void, EvidenceVerifyDto> = {
  method: "GET",
  path: ({ id }) => `/api/evidence/tasks/${id}/verify`,
  params: taskParams,
  response: verifyResponse,
};

// ── Receipts summary ─────────────────────────────────────────────────────────

const receiptsResponse = z.object({
  taskId: z.number().int().positive(),
  receipts: z.array(bundleReceiptRow),
  summary: z.object({
    total: z.number().int().nonnegative(),
    passing: z.number().int().nonnegative(),
    failing: z.number().int().nonnegative(),
    treeMismatch: z.number().int().nonnegative(),
  }),
});
export type EvidenceReceiptsDto = z.infer<typeof receiptsResponse>;

const receiptsOp: Operation<{ id: number }, void, void, EvidenceReceiptsDto> = {
  method: "GET",
  path: ({ id }) => `/api/evidence/tasks/${id}/receipts`,
  params: taskParams,
  response: receiptsResponse,
};

// ── Claims for a task (claim.assert + falsifiers) ────────────────────────────
// Complements sessions.claims (P2-U2): same store, but surfaces falsifiers from
// the claim payload for the SKU A dogfood surface.

const claimWithFalsifier = z.object({
  sessionId: z.number().int().positive(),
  sessionUid: z.string().nullable(),
  sessionUrn: z.string(),
  seq: z.number().int().positive(),
  actorId: z.string(),
  predicate: z.unknown(),
  fromSeq: z.number().int(),
  toSeq: z.number().int(),
  chainHash: z.string().nullable(),
  createdAt: z.string(),
  falsifiers: z.unknown().nullable(),
});
export type EvidenceClaimDto = z.infer<typeof claimWithFalsifier>;

/** One row of the checking waterfall (claim or classified gate receipt). */
const waterfallRow = z.object({
  key: z.string(),
  source: z.enum(["claim", "receipt"]),
  label: z.string(),
  tier: z.union([
    z.literal(0),
    z.literal(1),
    z.literal(2),
    z.literal(3),
    z.literal(4),
    z.null(),
  ]),
  /** Distinctive label from the data layer — the rendered-output probe asserts this. */
  tierLabel: z.string(),
  cost: z.enum(["no model", "model-judged", "undischarged"]),
  outcome: z.enum(["discharged", "failed", "undischarged", "judged"]),
});
export type EvidenceWaterfallRowDto = z.infer<typeof waterfallRow>;

const checkingWaterfall = z.object({
  rows: z.array(waterfallRow),
  total: z.number().int().nonnegative(),
  dischargedT0T2: z.number().int().nonnegative(),
  /** "N of M discharged at T0–T2 (no model)" — must match dischargedT0T2/total. */
  summary: z.string(),
});
export type EvidenceWaterfallDto = z.infer<typeof checkingWaterfall>;

const claimsResponse = z.object({
  taskId: z.number().int().positive(),
  claims: z.array(claimWithFalsifier),
});
export type EvidenceClaimsDto = z.infer<typeof claimsResponse>;

const claimsOp: Operation<{ id: number }, void, void, EvidenceClaimsDto> = {
  method: "GET",
  path: ({ id }) => `/api/evidence/tasks/${id}/claims`,
  params: taskParams,
  response: claimsResponse,
};

// ── Task SKU A surface (composite for dogfood detail) ────────────────────────

const runHistoryRow = z.object({
  key: z.string(),
  kind: z.enum(["claim", "receipt", "verdict", "artifact", "bundle", "landed"]),
  at: z.string().nullable(),
  actor: z.string().nullable(),
  label: z.string(),
  detail: z.string().nullable(),
  status: z.enum(["pass", "fail", "pending", "info"]),
});
export type EvidenceRunHistoryRowDto = z.infer<typeof runHistoryRow>;

const taskDetailResponse = z.object({
  taskId: z.number().int().positive(),
  title: z.string(),
  status: z.string(),
  projectSlug: z.string(),
  sectionSlug: z.string(),
  projectName: z.string().nullable(),
  ownerLabel: z.string().nullable(),
  verifierLabel: z.string().nullable(),
  consequenceTag: z.string().nullable(),
  consequenceBasis: z.string().nullable(),
  /** Dual-agent gate: pending when status=review; resolved when done. */
  verdict: z.enum(["pending", "accepted", "rejected", "edited"]),
  evidenceArtifact: z.object({
    found: z.boolean(),
    /** Open/copy link — same resource as tasks.evidenceArtifact. */
    href: z.string().nullable(),
    sessionUid: z.string().nullable(),
    seq: z.number().nullable(),
    verifier: z.string().nullable(),
    shipper: z.string().nullable(),
    emittedAt: z.string().nullable(),
    caller: z.string().nullable(),
    bodyHash: z.string().nullable(),
  }),
  bundle: z.union([
    z.object({
      found: z.literal(true),
	      bundleHash: z.string(),
	      signatureStatus: z.string(),
	      trust: bundleTrust,
	      byteLength: z.number().int().nonnegative(),
      claimCount: z.number().int().nonnegative(),
      receiptCount: z.number().int().nonnegative(),
      aidRef: z.string().nullable(),
      chainHead: z.string().nullable(),
    }),
    z.object({ found: z.literal(false), reason: z.string() }),
  ]),
  /** Offline-verify of the currently composed bundle (null if compose failed). */
  verify: verifyResult.nullable(),
  claims: z.array(claimWithFalsifier),
  receipts: z.array(bundleReceiptRow),
  /**
   * Dual-agent VERDICTS from `dod_judgment_receipts` — the approve/reject rows.
   *
   * ⚠️ These are NOT `receipts` above. That field is `gate_run_receipts`: which
   * commands ran and their exit codes. Only THIS field carries whether a human
   * or agent accepted or REJECTED the work. #45872: the detail surface rendered
   * the gate receipts and never read this table, so a task with five rejects in
   * the vault showed zero occurrences of the word "reject" — a rubber stamp and
   * a real rejection looked identical to a reviewer, which is precisely the
   * claim the verification product exists to make.
   */
  judgments: z.array(judgmentReceiptRow),
  /**
   * Run-level replay/history for the review door. This is composed from the
   * same claims, receipts, judgments, evidence artifact, bundle, and landing
   * rows in this payload, ordered as a compact task-level timeline.
   */
  runHistory: z.array(runHistoryRow),
  /**
   * Checking waterfall for this receipt: each claim (and classified gate
   * receipt) with the tier that discharged it, ordered cheap → expensive.
   * T3/T4 appear only when that tier actually ran. Task 46110.
   */
  waterfall: checkingWaterfall,
  /**
   * Derived residual-gap report — every in-scope claim the pack did not
   * discharge. `scope-unknown` when scope cannot be enumerated; that case
   * MUST NOT print "0 gaps".
   */
  residualGaps: residualGapReport,
  /**
   * Phase 3.C bijection trail: claim -> commit sha(s) -> receipts -> verdict ->
   * landed. `none-required` is intentionally distinct from `integrated`.
   */
  commits: z.array(
    z.object({
      sha: z.string(),
      linkKind: z.string().nullable(),
      linkedAt: z.string().nullable(),
    }),
  ),
  integrationState: z.string().nullable(),
  integratedAt: z.string().nullable(),
  integrationSha: z.string().nullable(),
  landed: z.object({
    state: z.string(),
    source: z.enum(["stamped", "computed"]),
    at: z.string().nullable(),
  }),
});
export type EvidenceTaskDetailDto = z.infer<typeof taskDetailResponse>;

const taskDetailOp: Operation<
  { id: number },
  void,
  void,
  EvidenceTaskDetailDto
> = {
  method: "GET",
  path: ({ id }) => `/api/evidence/tasks/${id}`,
  params: taskParams,
  response: taskDetailResponse,
};

// ── Stable programmatic verdict API (E12 / VER-008) ─────────────────────────

const platformVerdictResponse = z.object({
  taskId: z.number().int().positive(),
  source: z.literal("evidence-task-detail"),
  domain: z.string(),
  useCase: z.string(),
  verdict: z.enum(["pending", "accepted", "rejected", "edited"]),
  reasons: z.array(z.string()),
  findings: z.array(
    z.object({
      id: z.string(),
      severity: z.enum(["info", "warning", "critical"]),
      reason: z.string(),
      wouldDischarge: z.string().nullable(),
    }),
  ),
  receiptRefs: z.array(
    z.object({
      uid: z.string().nullable(),
      command: z.string(),
      exitCode: z.number().int(),
      binding: z.string().nullable(),
      treeHash: z.string(),
      createdAt: z.string(),
    }),
  ),
  externalOracles: z.array(
    z.object({
      predicate: z.string(),
      evidenceRef: z.string(),
      actorId: z.string(),
    }),
  ),
  bundle: z.union([
    z.object({
      found: z.literal(true),
      bundleHash: z.string(),
      trust: bundleTrust,
      verifyOk: z.boolean().nullable(),
    }),
    z.object({
      found: z.literal(false),
      reason: z.string(),
    }),
  ]),
});
export type EvidencePlatformVerdictDto = z.infer<typeof platformVerdictResponse>;

const platformVerdictOp: Operation<
  { id: number },
  void,
  void,
  EvidencePlatformVerdictDto
> = {
  method: "GET",
  path: ({ id }) => `/api/evidence/tasks/${id}/platform-verdict`,
  params: taskParams,
  response: platformVerdictResponse,
};

// ── Silent-approve rate (SKU A: must stay 0.0% with UI in the loop) ───────────

const silentApproveResponse = z.object({
  total: z.number().int().nonnegative(),
  silent: z.number().int().nonnegative(),
  rate: z.number(),
  /** Percent string for display (e.g. "0.0%"). */
  ratePct: z.string(),
  ok: z.boolean(),
  silentTaskIds: z.array(z.number().int()),
});
export type SilentApproveDto = z.infer<typeof silentApproveResponse>;

const silentApproveOp: Operation<void, void, void, SilentApproveDto> = {
  method: "GET",
  path: () => "/api/evidence/silent-approve",
  response: silentApproveResponse,
};

// ── MD-1 evidence pack (bundle + README + control map + verify) ──────────────

const packGetResponse = z.union([
  z.object({
    ok: z.literal(true),
    taskId: z.number().int().positive(),
    folderName: z.string(),
	    summary: z.string(),
	    trust: bundleTrust,
    /** Relative path → utf8 body (client writes zip/folder or multi-download). */
    files: z.record(z.string(), z.string()),
    fileNames: z.array(z.string()),
    /**
     * INV-8 — the export.emitted audit row written BEFORE this pack returned.
     * A success without this id is not a success (contract refuses it).
     */
    auditRowId: z.number().int().positive(),
    exportEmittedTotal: z.number().int().nonnegative(),
    bodyHash: z.string().min(1),
  }),
  z.object({
    ok: z.literal(false),
    taskId: z.number().int().positive(),
    reason: z.string(),
  }),
]);
export type EvidencePackGetDto = z.infer<typeof packGetResponse>;

const packGetOp: Operation<{ id: number }, void, void, EvidencePackGetDto> = {
  method: "GET",
  path: ({ id }) => `/api/evidence/tasks/${id}/pack`,
  params: taskParams,
  response: packGetResponse,
};

// ── Vault-wide receipt browser + rejection detail (Phase 3.A) ────────────────
// Reads `gate_run_receipts` and `excellent_ops_events` (kind=review.rejected)
// — the same rows 1.3/1.4 persist. No write path.

const receiptBrowseQuery = z.object({
  limit: z.coerce.number().int().positive().max(200).optional(),
  /** A dirty binding proves materially less than staged — never hide this. */
  binding: z.enum(["staged", "dirty"]).optional(),
  exit: z.enum(["pass", "fail"]).optional(),
  q: z.string().optional(),
});

const browseReceiptRow = bundleReceiptRow.extend({
  id: z.number().int().positive(),
  taskId: z.number().int().positive(),
  taskTitle: z.string(),
  taskStatus: z.string(),
});
export type EvidenceBrowseReceiptDto = z.infer<typeof browseReceiptRow>;

const receiptBrowseResponse = z.object({
  receipts: z.array(browseReceiptRow),
  summary: z.object({
    total: z.number().int().nonnegative(),
    passing: z.number().int().nonnegative(),
    failing: z.number().int().nonnegative(),
    staged: z.number().int().nonnegative(),
    dirty: z.number().int().nonnegative(),
    shown: z.number().int().nonnegative(),
  }),
});
export type EvidenceReceiptBrowseDto = z.infer<typeof receiptBrowseResponse>;

const receiptBrowseOp: Operation<
  void,
  z.infer<typeof receiptBrowseQuery>,
  void,
  EvidenceReceiptBrowseDto
> = {
  method: "GET",
  path: () => "/api/evidence/receipts",
  query: receiptBrowseQuery,
  response: receiptBrowseResponse,
};

/** What happened after this reject — the credibility asset, not a ✓. */
const rejectNextKind = z.enum([
  "standing",
  "reworked",
  "superseded",
  "later-reject",
]);
export type EvidenceRejectNextKind = z.infer<typeof rejectNextKind>;

const rejectBrowseQuery = z.object({
  limit: z.coerce.number().int().positive().max(200).optional(),
  standing: z.enum(["yes", "no"]).optional(),
  q: z.string().optional(),
});

const rejectBrowseRow = z.object({
  opsId: z.number().int().positive(),
  taskId: z.number().int().positive(),
  taskTitle: z.string().nullable(),
  taskStatus: z.string().nullable(),
  bounce: z.number().int().nonnegative(),
  verifier: z.string(),
  shipper: z.string().nullable(),
  note: z.string(),
  rejectedAt: z.string(),
  nextKind: rejectNextKind,
});
export type EvidenceRejectBrowseRowDto = z.infer<typeof rejectBrowseRow>;

const rejectBrowseResponse = z.object({
  rejects: z.array(rejectBrowseRow),
  summary: z.object({
    total: z.number().int().nonnegative(),
    standing: z.number().int().nonnegative(),
    reworked: z.number().int().nonnegative(),
    superseded: z.number().int().nonnegative(),
    shown: z.number().int().nonnegative(),
  }),
});
export type EvidenceRejectBrowseDto = z.infer<typeof rejectBrowseResponse>;

const rejectBrowseOp: Operation<
  void,
  z.infer<typeof rejectBrowseQuery>,
  void,
  EvidenceRejectBrowseDto
> = {
  method: "GET",
  path: () => "/api/evidence/rejects",
  query: rejectBrowseQuery,
  response: rejectBrowseResponse,
};

const rejectDetailResponse = z.object({
  opsId: z.number().int().positive(),
  taskId: z.number().int().positive(),
  taskTitle: z.string().nullable(),
  taskStatus: z.string().nullable(),
  bounce: z.number().int().nonnegative(),
  verifier: z.string(),
  shipper: z.string().nullable(),
  note: z.string(),
  rejectedAt: z.string(),
  source: z.string().nullable(),
  next: z.object({
    kind: rejectNextKind,
    reShipped: z.boolean(),
    reworked: z.boolean(),
    noRework: z.boolean(),
    superseded: z.boolean(),
    laterRejects: z.number().int().nonnegative(),
    laterReceipts: z.number().int().nonnegative(),
    reAuditCount: z.number().int().nonnegative(),
    nextVerdict: z
      .object({
        verdict: z.string(),
        verifier: z.string().nullable(),
        createdAt: z.string(),
      })
      .nullable(),
    supersededBy: z
      .object({
        opsId: z.number().int().positive(),
        verifier: z.string().nullable(),
        note: z.string().nullable(),
        at: z.string(),
      })
      .nullable(),
  }),
});
export type EvidenceRejectDetailDto = z.infer<typeof rejectDetailResponse>;

const rejectDetailOp: Operation<
  { id: number },
  void,
  void,
  EvidenceRejectDetailDto
> = {
  method: "GET",
  path: ({ id }) => `/api/evidence/rejects/${id}`,
  params: taskParams,
  response: rejectDetailResponse,
};

// ── SOC 2 preflight package run ─────────────────────────────────────────────

const soc2PreflightStatus = z.enum([
  "Verified",
  "Exception",
  "Needs determination",
  "Stale/not tested",
]);
export type EvidenceSoc2PreflightStatusDto = z.infer<typeof soc2PreflightStatus>;

const soc2ActorRole = z.enum(["owner", "operator", "control_owner", "reviewer", "auditor"]);
const soc2AccessContextQuery = z.object({
  actorTenantId: z.string().min(1).optional(),
  actorRole: soc2ActorRole.optional(),
});

const soc2EvidenceArtifact = z.object({
  id: z.string(),
  type: z.enum(["file", "url", "screenshot", "export", "ticket", "snapshot"]),
  title: z.string(),
  source: z.string(),
  contentHash: z.string(),
  collectedAt: z.string(),
  actor: z.string(),
  observations: z
    .array(
      z.object({
        requirementId: z.string(),
        checkId: z.string(),
        type: z
          .enum([
            "check_status",
            "check_reason",
            "artifact_freshness",
            "population_reconciliation",
          ])
          .optional(),
        subject: z.string().optional(),
        value: z.string(),
        parseConfidence: z.number().min(0).max(1).optional(),
      }),
    )
    .optional(),
});

const soc2Check = z.object({
  id: z.string(),
  requirementId: z.string(),
  status: z.enum(["pass", "fail", "unknown", "not_run"]),
  reason: z.string(),
  severity: z.enum(["low", "medium", "high", "critical"]),
  evidenceRefs: z.array(z.string()),
});

const soc2PopulationReconciliationInput = z.object({
  id: z.string(),
  requirementId: z.string(),
  population: z.string(),
  authoritativeSource: z.string(),
  testedSource: z.string(),
  authoritativeSubjects: z.array(z.string()),
  testedSubjects: z.array(z.string()),
  evidenceRefs: z.array(z.string()),
  severity: soc2Check.shape.severity,
});

const soc2ControlInput = z.object({
  id: z.string(),
  tsc: z.string(),
  family: z.enum(["identity-mfa", "termination-access-removal", "periodic-access-review"]),
  title: z.string(),
  owner: z.string(),
  frequency: z.string(),
  controlText: z.string(),
  requirements: z.array(z.object({ id: z.string(), text: z.string() })),
  evidence: z.array(soc2EvidenceArtifact),
  checks: z.array(soc2Check),
  populationReconciliations: z.array(soc2PopulationReconciliationInput).optional(),
});

const soc2PreflightPackage = z.object({
  schemaVersion: z.literal(1),
  organization: z.object({ id: z.string(), name: z.string() }),
  auditPeriod: z.object({ id: z.string(), start: z.string(), end: z.string() }),
  scope: z.object({
    systems: z.array(z.string()),
    repositories: z.array(z.string()),
    populations: z.array(z.string()),
  }),
  controls: z.array(soc2ControlInput),
});
export type EvidenceSoc2PreflightPackageDto = z.infer<typeof soc2PreflightPackage>;

const soc2Finding = z.object({
  controlId: z.string(),
  tsc: z.string(),
  family: soc2ControlInput.shape.family,
  status: soc2PreflightStatus,
  owner: z.string(),
  requirementId: z.string().nullable(),
  checkId: z.string().nullable(),
  reason: z.string(),
  severity: soc2Check.shape.severity,
  evidenceRefs: z.array(z.string()),
  policyVersion: z.string(),
  action: z.string(),
});
export type EvidenceSoc2FindingDto = z.infer<typeof soc2Finding>;

const soc2EvidenceObservation = z.object({
  schemaVersion: z.literal(1),
  id: z.string(),
  controlId: z.string(),
  requirementId: z.string(),
  checkId: z.string(),
  artifactId: z.string(),
  type: z.enum([
    "check_status",
    "check_reason",
    "artifact_freshness",
    "population_reconciliation",
  ]),
  subject: z.string(),
  value: z.string(),
  period: soc2PreflightPackage.shape.auditPeriod,
  provenance: z.object({
    artifactId: z.string(),
    contentHash: z.string(),
    source: z.string(),
    collectedAt: z.string(),
    actor: z.string(),
  }),
  extractorVersion: z.string(),
  parseConfidence: z.number().min(0).max(1),
  createdAt: z.string(),
});
export type EvidenceSoc2ObservationDto = z.infer<typeof soc2EvidenceObservation>;

const soc2TemplateId = z.enum([
  "soc2-identity-mfa",
  "soc2-termination-access-removal",
  "soc2-periodic-access-review",
]);
export type EvidenceSoc2TemplateIdDto = z.infer<typeof soc2TemplateId>;

const soc2ControlTemplateMapping = z.object({
  schemaVersion: z.literal(1),
  controlId: z.string(),
  templateId: soc2TemplateId.nullable(),
  templateName: z.string(),
  family: soc2ControlInput.shape.family,
  confidence: z.number().min(0).max(1),
  status: z.enum(["mapped", "unmapped"]),
  mappedAt: z.string(),
  mapper: z.enum(["deterministic-family-v1", "reviewer"]),
  reversible: z.literal(true),
  customerControlText: z.string(),
  requirementIds: z.array(z.string()),
  sourceExpectations: z.array(z.string()),
  unknownRoutes: z.array(z.string()),
});
export type EvidenceSoc2ControlTemplateMappingDto = z.infer<
  typeof soc2ControlTemplateMapping
>;

const soc2PolicyCheckRun = z.object({
  schemaVersion: z.literal(1),
  policyId: z.string(),
  policyVersion: z.string(),
  controlId: z.string(),
  checkId: z.string(),
  requirementId: z.string(),
  kind: z.enum([
    "evidence_exists",
    "observation_status",
    "population_reconciled",
    "temporal_threshold",
    "approval_identity",
    "arithmetic_reconciliation",
  ]),
  result: z.enum(["pass", "fail", "unknown", "not_run"]),
  reason: z.string(),
  severity: soc2Check.shape.severity,
  consequence: z.enum(["low", "medium", "high", "critical"]),
  evidenceRefs: z.array(z.string()),
  observationIds: z.array(z.string()),
  engineVersion: z.string(),
});

const soc2PolicyEvaluation = z.object({
  schemaVersion: z.literal(1),
  controlId: z.string(),
  policy: z.object({
    policyId: z.string(),
    version: z.string(),
    canonicalHash: z.string(),
  }),
  verdict: soc2PreflightStatus,
  checkRuns: z.array(soc2PolicyCheckRun),
});

const soc2PopulationReconciliationResult = z.object({
  schemaVersion: z.literal(1),
  id: z.string(),
  controlId: z.string(),
  requirementId: z.string(),
  population: z.string(),
  authoritativeSource: z.string(),
  testedSource: z.string(),
  authoritativeCount: z.number().int().nonnegative(),
  testedCount: z.number().int().nonnegative(),
  matchedCount: z.number().int().nonnegative(),
  missingSubjects: z.array(z.string()),
  unexpectedSubjects: z.array(z.string()),
  coverage: z.number().min(0).max(1),
  status: soc2PreflightStatus,
  severity: soc2Check.shape.severity,
  evidenceRefs: z.array(z.string()),
  reason: z.string(),
});

const soc2ControlResult = z.object({
  controlId: z.string(),
  tsc: z.string(),
  title: z.string(),
  controlText: z.string(),
  family: soc2ControlInput.shape.family,
  status: soc2PreflightStatus,
  owner: z.string(),
  evidenceCount: z.number().int().nonnegative(),
  checkCount: z.number().int().nonnegative(),
  reason: z.string(),
  policyVersion: z.string(),
  requirements: soc2ControlInput.shape.requirements,
  requirementIds: z.array(z.string()),
  evidenceArtifacts: z.array(soc2EvidenceArtifact),
  observations: z.array(soc2EvidenceObservation),
  populationReconciliations: z.array(soc2PopulationReconciliationResult),
  templateMapping: soc2ControlTemplateMapping,
  policyEvaluation: soc2PolicyEvaluation,
  evidenceRefs: z.array(z.string()),
  findings: z.array(soc2Finding),
});

const soc2VerificationReceipt = z.object({
  schemaVersion: z.literal(1),
  id: z.string(),
  runId: z.string(),
  controlId: z.string(),
  status: soc2PreflightStatus,
  policyVersion: z.string(),
  policyHash: z.string(),
  engineVersion: z.string(),
  issuedAt: z.string(),
  evidenceHashes: z.array(
    z.object({
      evidenceRef: z.string(),
      contentHash: z.string(),
    }),
  ),
  checkIds: z.array(z.string()),
  findingIds: z.array(z.string()),
  reviewDecisionIds: z.array(z.string()),
  observationCorrectionIds: z.array(z.string()),
  parentReceiptId: z.string().nullable(),
  canonicalHash: z.string(),
  signature: z.object({
    status: z.literal("unsigned-dev"),
    keyId: z.string(),
    algorithm: z.literal("sha256"),
  }),
});
export type EvidenceSoc2VerificationReceiptDto = z.infer<typeof soc2VerificationReceipt>;

const soc2ReviewDecisionKind = z.enum([
  "accept",
  "record_exception",
  "request_evidence",
  "investigate_remediate",
]);
export type EvidenceSoc2ReviewDecisionKindDto = z.infer<typeof soc2ReviewDecisionKind>;

const soc2ReviewDecision = z.object({
  schemaVersion: z.literal(1),
  id: z.string(),
  runId: z.string(),
  controlId: z.string(),
  findingId: z.string().nullable(),
  decision: soc2ReviewDecisionKind,
  actor: z.string(),
  decidedAt: z.string(),
  rationale: z.string(),
  policyVersion: z.string(),
  evidenceRefs: z.array(z.string()),
  parentReceiptId: z.string().nullable(),
  receiptId: z.string(),
});
export type EvidenceSoc2ReviewDecisionDto = z.infer<typeof soc2ReviewDecision>;

const soc2ReviewResolution = z.object({
  schemaVersion: z.literal(1),
  id: z.string(),
  runId: z.string(),
  controlId: z.string(),
  findingId: z.string().nullable(),
  decisionId: z.string(),
  decision: soc2ReviewDecisionKind,
  state: z.enum(["accepted", "exception_recorded", "evidence_requested", "investigation_open"]),
  previousStatus: soc2PreflightStatus,
  resolvedStatus: soc2PreflightStatus,
  reason: z.string(),
  action: z.string(),
  requiresReverification: z.boolean(),
  parentReceiptId: z.string().nullable(),
  receiptId: z.string(),
  evidenceRefs: z.array(z.string()),
  decidedAt: z.string(),
});
export type EvidenceSoc2ReviewResolutionDto = z.infer<typeof soc2ReviewResolution>;

const soc2ObservationCorrection = z.object({
  schemaVersion: z.literal(1),
  id: z.string(),
  runId: z.string(),
  observationId: z.string(),
  controlId: z.string(),
  artifactId: z.string(),
  actor: z.string(),
  correctedAt: z.string(),
  correctedValue: z.string(),
  rationale: z.string(),
  policyVersion: z.string(),
  parentReceiptId: z.string().nullable(),
  receiptId: z.string(),
});
export type EvidenceSoc2ObservationCorrectionDto = z.infer<
  typeof soc2ObservationCorrection
>;

const soc2TemplateMappingEvent = z.object({
  schemaVersion: z.literal(1),
  id: z.string(),
  runId: z.string(),
  controlId: z.string(),
  previousTemplateId: soc2TemplateId.nullable(),
  nextTemplateId: soc2TemplateId.nullable(),
  actor: z.string(),
  rationale: z.string(),
  createdAt: z.string(),
  customerControlText: z.string(),
});
export type EvidenceSoc2TemplateMappingEventDto = z.infer<
  typeof soc2TemplateMappingEvent
>;

const soc2PreflightReport = z.object({
  schemaVersion: z.literal(1),
  organization: soc2PreflightPackage.shape.organization,
  auditPeriod: soc2PreflightPackage.shape.auditPeriod,
  scope: soc2PreflightPackage.shape.scope,
  policyVersion: z.string(),
  generatedAt: z.string(),
  summary: z.object({
    totalControls: z.number().int().nonnegative(),
    Verified: z.number().int().nonnegative(),
    Exception: z.number().int().nonnegative(),
    "Needs determination": z.number().int().nonnegative(),
    "Stale/not tested": z.number().int().nonnegative(),
    verifiedCoverage: z.number().min(0).max(1),
    unknownRate: z.number().min(0).max(1),
    timeToFirstFindingMinutes: z.number().nonnegative(),
  }),
  controls: z.array(soc2ControlResult),
  findings: z.array(soc2Finding),
  populationReconciliations: z.array(soc2PopulationReconciliationResult),
  receipts: z.array(soc2VerificationReceipt),
  exportNotice: z.string(),
});
export type EvidenceSoc2PreflightReportDto = z.infer<typeof soc2PreflightReport>;

const soc2AuditPreparationExportPacket = z.object({
  format: z.literal("excellent-soc2-audit-preparation-export"),
  schemaVersion: z.literal(1),
  organization: soc2PreflightPackage.shape.organization,
  auditPeriod: soc2PreflightPackage.shape.auditPeriod,
  scope: soc2PreflightPackage.shape.scope,
  policyVersion: z.string(),
  generatedAt: z.string(),
  summary: soc2PreflightReport.shape.summary.extend({
    totalFindings: z.number().int().nonnegative(),
    totalExceptions: z.number().int().nonnegative(),
    scopedEvidenceRefs: z.number().int().nonnegative(),
    excludedEvidenceRefs: z.number().int().nonnegative(),
  }),
  scopedControls: z.array(
    z.object({
      controlId: z.string(),
      tsc: z.string(),
      title: z.string(),
      controlText: z.string(),
      family: soc2ControlInput.shape.family,
      owner: z.string(),
      status: soc2PreflightStatus,
      requirements: soc2ControlInput.shape.requirements,
      templateMapping: soc2ControlTemplateMapping,
      policyEvaluation: soc2PolicyEvaluation,
      populationReconciliations: z.array(soc2PopulationReconciliationResult),
      requirementIds: z.array(z.string()),
      evidenceRefs: z.array(z.string()),
      findingIds: z.array(z.string()),
    }),
  ),
  evidenceMetadataRefs: z.array(
    z.object({
      evidenceRef: z.string(),
      artifact: soc2EvidenceArtifact,
      controlIds: z.array(z.string()),
      findingIds: z.array(z.string()),
      retention: z.object({
        state: z.enum(["retained", "expires_soon", "expired"]),
        retainUntil: z.string(),
        basis: z.string(),
      }),
      exportDecision: z.object({
        state: z.literal("included"),
        reason: z.literal("included"),
      }),
    }),
  ),
  excludedEvidenceMetadataRefs: z.array(
    z.object({
      evidenceRef: z.string(),
      artifact: soc2EvidenceArtifact.nullable(),
      controlIds: z.array(z.string()),
      findingIds: z.array(z.string()),
      retention: z
        .object({
          state: z.enum(["retained", "expires_soon", "expired"]),
          retainUntil: z.string(),
          basis: z.string(),
        })
        .nullable(),
      exportDecision: z.object({
        state: z.literal("excluded"),
        reason: z.enum(["permission_excluded", "retention_expired"]),
      }),
    }),
  ),
  exportPolicy: z.object({
    schemaVersion: z.literal(1),
    includeExpiredEvidence: z.boolean(),
    allowedEvidenceRefs: z.array(z.string()).nullable(),
    scopedEvidenceRefs: z.number().int().nonnegative(),
    excludedEvidenceRefs: z.number().int().nonnegative(),
    receiptsRedacted: z.boolean(),
  }),
  populationReconciliations: z.array(soc2PopulationReconciliationResult),
  findings: z.array(soc2Finding),
  exceptions: z.array(soc2Finding),
  receipts: z.array(soc2VerificationReceipt),
  boundaryNotice: z.string(),
});
export type EvidenceSoc2AuditPreparationExportPacketDto = z.infer<
  typeof soc2AuditPreparationExportPacket
>;

const soc2AuditPreparationExportQuery = z.object({
  evidenceRefs: z.string().optional(),
  includeExpiredEvidence: z.coerce.boolean().optional(),
});

const soc2PreflightRunBody = z.object({
  package: soc2PreflightPackage,
  generatedAt: z.string().optional(),
  policyVersion: z.string().optional(),
});
export type EvidenceSoc2PreflightRunBodyDto = z.infer<typeof soc2PreflightRunBody>;

const soc2PreflightRunSummary = z.object({
  id: z.string(),
  label: z.string(),
  organizationName: z.string(),
  auditPeriodId: z.string(),
  createdAt: z.string(),
  policyVersion: z.string(),
  summary: soc2PreflightReport.shape.summary,
});
export type EvidenceSoc2PreflightRunSummaryDto = z.infer<typeof soc2PreflightRunSummary>;

const soc2PreflightStoredRun = z.object({
  id: z.string(),
  label: z.string(),
  createdAt: z.string(),
  source: z.string(),
  packageHash: z.string(),
  report: soc2PreflightReport,
  reviewDecisions: z.array(soc2ReviewDecision),
  reviewResolutions: z.array(soc2ReviewResolution),
  observationCorrections: z.array(soc2ObservationCorrection),
  templateMappingEvents: z.array(soc2TemplateMappingEvent),
});
export type EvidenceSoc2PreflightStoredRunDto = z.infer<typeof soc2PreflightStoredRun>;

const soc2PreflightImportBody = soc2PreflightRunBody.extend({
  label: z.string().max(160).optional(),
});
export type EvidenceSoc2PreflightImportBodyDto = z.infer<typeof soc2PreflightImportBody>;

const soc2PreflightImportResponse = z.object({
  run: soc2PreflightStoredRun,
});
export type EvidenceSoc2PreflightImportDto = z.infer<typeof soc2PreflightImportResponse>;

const soc2BatchControlProgress = z.object({
  controlId: z.string(),
  status: z.enum(["not_started", "completed", "failed"]),
  receiptId: z.string().nullable(),
  findingCount: z.number().int().nonnegative(),
});
export type EvidenceSoc2BatchControlProgressDto = z.infer<typeof soc2BatchControlProgress>;

const soc2PreflightBatchJob = z.object({
  schemaVersion: z.literal(1),
  id: z.string(),
  idempotencyKey: z.string(),
  status: z.enum(["queued", "running", "completed", "failed"]),
  packageHash: z.string(),
  policyVersion: z.string(),
  runId: z.string().nullable(),
  attempts: z.number().int().positive(),
  createdAt: z.string(),
  updatedAt: z.string(),
  error: z.string().nullable(),
  progress: z.object({
    totalControls: z.number().int().nonnegative(),
    completedControls: z.number().int().nonnegative(),
    failedControls: z.number().int().nonnegative(),
  }),
  controls: z.array(soc2BatchControlProgress),
});
export type EvidenceSoc2PreflightBatchJobDto = z.infer<typeof soc2PreflightBatchJob>;

const soc2PreflightBatchRunBody = soc2PreflightRunBody.extend({
  label: z.string().max(160).optional(),
  idempotencyKey: z.string().min(1).max(200),
  simulateFailureAfterControls: z.number().int().nonnegative().optional(),
});
export type EvidenceSoc2PreflightBatchRunBodyDto = z.infer<typeof soc2PreflightBatchRunBody>;

const soc2PreflightBatchRunResponse = z.object({
  job: soc2PreflightBatchJob,
  run: soc2PreflightStoredRun.nullable(),
  reused: z.boolean(),
});
export type EvidenceSoc2PreflightBatchRunDto = z.infer<typeof soc2PreflightBatchRunResponse>;

const soc2PreflightBatchJobsResponse = z.object({
  jobs: z.array(soc2PreflightBatchJob),
});
export type EvidenceSoc2PreflightBatchJobsDto = z.infer<typeof soc2PreflightBatchJobsResponse>;

const soc2PreflightRunsResponse = z.object({
  runs: z.array(soc2PreflightRunSummary),
});
export type EvidenceSoc2PreflightRunsDto = z.infer<typeof soc2PreflightRunsResponse>;

const soc2ImportOnboardingNextAction = z.enum([
  "create-import",
  "finish-mapping",
  "fix-preview-blockers",
  "run-first-verification",
  "review-first-finding",
]);

const soc2PreflightImportOnboardingStateResponse = z.object({
  tenantId: z.string(),
  generatedAt: z.string(),
  persistedJobCount: z.number().int().nonnegative(),
  mappingDraftCount: z.number().int().nonnegative(),
  activeJobIds: z.array(z.string()),
  readyJobIds: z.array(z.string()),
  latestJobId: z.string().nullable(),
  latestDraftId: z.string().nullable(),
  nextAction: soc2ImportOnboardingNextAction,
  blockedReasons: z.array(z.string()),
  timeToFirstFindingMinutes: z.number().int().nonnegative().nullable(),
});
export type EvidenceSoc2PreflightImportOnboardingStateDto = z.infer<
  typeof soc2PreflightImportOnboardingStateResponse
>;

const soc2PolicyStudioProposalStatus = z.enum(["blocked", "awaiting_activation", "activated"]);
const soc2PolicyStudioAction = z.enum([
  "list_proposals",
  "activate_proposal",
  "repair_proposal",
  "view_audit_record",
]);

const soc2PolicyStudioAuditRecord = z.object({
  actorId: z.string(),
  actorRole: z.string(),
  activatedAt: z.string(),
  rationale: z.string(),
  auditEvidenceRefs: z.array(z.string()),
});

const soc2PolicyStudioWorkflowItem = z.object({
  schemaVersion: z.literal(1),
  proposalId: z.string(),
  tenantId: z.string(),
  controlId: z.string(),
  previousPolicyId: z.string(),
  proposedPolicyId: z.string(),
  status: soc2PolicyStudioProposalStatus,
  requiredAction: z.enum(["fix_proposal", "collect_human_activation", "none"]),
  activationIntent: z.enum(["tighten", "neutral", "loosen"]),
  impactFingerprint: z.string(),
  impacts: z.array(
    z.object({
      frozenCaseId: z.string(),
      previousStatus: z.string(),
      proposedStatus: z.string(),
      statusChanged: z.boolean(),
    }),
  ),
  blockedReasons: z.array(z.string()),
  warnings: z.array(z.string()),
  auditRecord: soc2PolicyStudioAuditRecord.nullable(),
});

const soc2PolicyStudioWorkflow = z.object({
  schemaVersion: z.literal(1),
  tenantId: z.string(),
  controlId: z.string(),
  generatedAt: z.string(),
  proposals: z.array(soc2PolicyStudioWorkflowItem),
  activationAuditRecords: z.array(
    z.object({
      proposalId: z.string(),
      activatedPolicyId: z.string(),
      impactFingerprint: z.string(),
      auditRecord: soc2PolicyStudioAuditRecord,
    }),
  ),
  summary: z.object({
    totalProposals: z.number().int().nonnegative(),
    blocked: z.number().int().nonnegative(),
    awaitingActivation: z.number().int().nonnegative(),
    activated: z.number().int().nonnegative(),
  }),
});

const soc2PreflightPolicyStudioStateResponse = z.object({
  tenantId: z.string(),
  generatedAt: z.string(),
  runId: z.string(),
  controlId: z.string().nullable(),
  actorRole: z.string(),
  workflow: soc2PolicyStudioWorkflow,
  actions: z.array(
    z.object({
      proposalId: z.string(),
      action: soc2PolicyStudioAction,
      ok: z.boolean(),
      next: z.enum(["return_workflow", "activate_policy", "open_repair", "return_audit_record", "refuse"]),
      blockers: z.array(z.string()),
    }),
  ),
});
export type EvidenceSoc2PreflightPolicyStudioStateDto = z.infer<
  typeof soc2PreflightPolicyStudioStateResponse
>;

const soc2AccessAuditDecision = z.enum(["allowed", "denied"]);
const soc2AccessAuditEvent = z.object({
  schemaVersion: z.literal(1),
  seq: z.number().int().positive(),
  id: z.string(),
  runId: z.string(),
  organizationId: z.string(),
  actorTenantId: z.string().nullable(),
  actorRole: soc2ActorRole.nullable(),
  action: z.string(),
  decision: soc2AccessAuditDecision,
  reason: z.string(),
  createdAt: z.string(),
  prevHash: z.string().nullable(),
  hash: z.string(),
});
export type EvidenceSoc2AccessAuditEventDto = z.infer<typeof soc2AccessAuditEvent>;

const soc2AccessAuditQuery = soc2AccessContextQuery.extend({
  runId: z.string().optional(),
  action: z.string().optional(),
  decision: soc2AccessAuditDecision.optional(),
  limit: z.coerce.number().int().positive().max(200).optional(),
});

const soc2AccessAuditResponse = z.object({
  events: z.array(soc2AccessAuditEvent),
  summary: z.object({
    total: z.number().int().nonnegative(),
    shown: z.number().int().nonnegative(),
    denied: z.number().int().nonnegative(),
    allowed: z.number().int().nonnegative(),
  }),
});
export type EvidenceSoc2AccessAuditDto = z.infer<typeof soc2AccessAuditResponse>;

const soc2CsvParseIssue = z.object({
  row: z.number().int().positive(),
  field: z.string(),
  severity: z.enum(["error", "warning"]),
  message: z.string(),
});
export type EvidenceSoc2CsvParseIssueDto = z.infer<typeof soc2CsvParseIssue>;

const soc2CsvImportBody = z.object({
  csv: z.string().min(1),
  label: z.string().max(160).optional(),
  organizationId: z.string().min(1),
  organizationName: z.string().min(1),
  auditPeriodId: z.string().min(1),
  auditPeriodStart: z.string().min(1),
  auditPeriodEnd: z.string().min(1),
  generatedAt: z.string().optional(),
  policyVersion: z.string().optional(),
});
export type EvidenceSoc2CsvImportBodyDto = z.infer<typeof soc2CsvImportBody>;

const soc2XlsxImportBody = soc2CsvImportBody.omit({ csv: true }).extend({
  xlsxBase64: z.string().min(1),
});
export type EvidenceSoc2XlsxImportBodyDto = z.infer<typeof soc2XlsxImportBody>;

const soc2CsvPreviewResponse = z.object({
  ok: z.boolean(),
  errors: z.array(soc2CsvParseIssue),
  warnings: z.array(soc2CsvParseIssue),
  rowCount: z.number().int().nonnegative(),
  controlCount: z.number().int().nonnegative(),
  package: soc2PreflightPackage.nullable(),
  report: soc2PreflightReport.nullable(),
});
export type EvidenceSoc2CsvPreviewDto = z.infer<typeof soc2CsvPreviewResponse>;
export type EvidenceSoc2XlsxPreviewDto = z.infer<typeof soc2CsvPreviewResponse>;

const soc2ReplayDifference = z.object({
  controlId: z.string(),
  field: z.enum(["status", "receipt", "finding_count", "evidence_hashes"]),
  expected: z.string(),
  actual: z.string(),
});
export type EvidenceSoc2ReplayDifferenceDto = z.infer<typeof soc2ReplayDifference>;

const soc2ReplayResponse = z.object({
  ok: z.boolean(),
  replayedAt: z.string(),
  originalGeneratedAt: z.string(),
  policyVersion: z.string(),
  totalControls: z.number().int().nonnegative(),
  differences: z.array(soc2ReplayDifference),
  report: soc2PreflightReport,
});
export type EvidenceSoc2ReplayDto = z.infer<typeof soc2ReplayResponse>;

const soc2ReplayQuery = z.object({
  includeCorrections: z.coerce.boolean().optional(),
});

const soc2Phase1FamilyMetric = z.object({
  family: soc2ControlInput.shape.family,
  totalControls: z.number().int().nonnegative(),
  verifiedControlCoverage: z.number().min(0).max(1),
  unknownRate: z.number().min(0).max(1),
  totalFindings: z.number().int().nonnegative(),
  reviewerOverrideRate: z.number().min(0).max(1),
  acceptedFindings: z.number().int().nonnegative(),
  requestedEvidence: z.number().int().nonnegative(),
  remediationInvestigations: z.number().int().nonnegative(),
  populationReconciliationCoverage: z.number().min(0).max(1).nullable(),
});

const soc2ReviewOutcomeSummary = z.object({
  totalReviewed: z.number().int().nonnegative(),
  acceptedFindings: z.number().int().nonnegative(),
  acceptedVerifiedControls: z.number().int().nonnegative(),
  recordedExceptions: z.number().int().nonnegative(),
  requestedEvidence: z.number().int().nonnegative(),
  remediationInvestigations: z.number().int().nonnegative(),
  falseAcceptReopens: z.number().int().nonnegative(),
});

const soc2DesignPartnerProofOfValue = z.object({
  baselineReviewTimeMinutes: z.number().nonnegative(),
  excellentReviewTimeMinutes: z.number().nonnegative().nullable(),
  reviewTimeSavedMinutes: z.number().nonnegative().nullable(),
  reviewTimeReductionRate: z.number().min(0).max(1).nullable(),
  acceptedFindingLabels: z.number().int().nonnegative(),
  rejectedFindingLabels: z.number().int().nonnegative(),
  phaseGateStopSignals: z.array(z.string()),
  comparisonBasis: z.string(),
});

const soc2Phase1Scorecard = z.object({
  schemaVersion: z.literal(1),
  auditPeriodId: z.string(),
  organizationName: z.string(),
  generatedAt: z.string(),
  policyVersion: z.string(),
  totalControls: z.number().int().nonnegative(),
  totalFindings: z.number().int().nonnegative(),
  verifiedControlCoverage: z.number().min(0).max(1),
  materialFalseAccepts: z.number().int().nonnegative(),
  unknownRate: z.number().min(0).max(1),
  reviewerOverrideRate: z.number().min(0).max(1),
  medianReviewTimeMinutes: z.number().nonnegative().nullable(),
  timeToFirstFindingMinutes: z.number().nonnegative(),
  populationReconciliationCoverage: z.number().min(0).max(1).nullable(),
  receiptReplaySuccess: z.number().min(0).max(1).nullable(),
  silentApproveRate: z.number().min(0).max(1),
  reviewOutcomeLabels: soc2ReviewOutcomeSummary,
  designPartnerProofOfValue: soc2DesignPartnerProofOfValue,
  families: z.array(soc2Phase1FamilyMetric),
  boundaryNotice: z.string(),
});
export type EvidenceSoc2Phase1ScorecardDto = z.infer<typeof soc2Phase1Scorecard>;

const soc2EvidenceInventoryArtifact = z.object({
  schemaVersion: z.literal(1),
  artifact: soc2EvidenceArtifact,
  controlIds: z.array(z.string()),
  findingIds: z.array(z.string()),
  observationIds: z.array(z.string()),
  observationCount: z.number().int().nonnegative(),
  freshness: z.enum(["fresh", "stale", "unknown"]),
  sourceHealth: z.enum(["healthy", "warning", "blocked"]),
  retention: z.object({
    state: z.enum(["retained", "expires_soon", "expired"]),
    retainUntil: z.string(),
    basis: z.string(),
  }),
  provenance: z.array(
    z.object({
      observationId: z.string(),
      controlId: z.string(),
      requirementId: z.string(),
      checkId: z.string(),
      type: soc2EvidenceObservation.shape.type,
      value: z.string(),
      source: z.string(),
      contentHash: z.string(),
      collectedAt: z.string(),
      actor: z.string(),
      extractorVersion: z.string(),
      parseConfidence: z.number().min(0).max(1),
    }),
  ),
});

const soc2EvidenceInventory = z.object({
  schemaVersion: z.literal(1),
  organization: soc2PreflightPackage.shape.organization,
  auditPeriod: soc2PreflightPackage.shape.auditPeriod,
  generatedAt: z.string(),
  policyVersion: z.string(),
  summary: z.object({
    totalArtifacts: z.number().int().nonnegative(),
    freshArtifacts: z.number().int().nonnegative(),
    staleArtifacts: z.number().int().nonnegative(),
    blockedSources: z.number().int().nonnegative(),
    retainedArtifacts: z.number().int().nonnegative(),
    expiringArtifacts: z.number().int().nonnegative(),
    expiredArtifacts: z.number().int().nonnegative(),
    observationCount: z.number().int().nonnegative(),
  }),
  artifacts: z.array(soc2EvidenceInventoryArtifact),
});
export type EvidenceSoc2EvidenceInventoryDto = z.infer<typeof soc2EvidenceInventory>;

const soc2SourceAlert = z.object({
  schemaVersion: z.literal(1),
  id: z.string(),
  runGeneratedAt: z.string(),
  organization: soc2PreflightPackage.shape.organization,
  auditPeriod: soc2PreflightPackage.shape.auditPeriod,
  policyVersion: z.string(),
  evidenceRef: z.string(),
  source: z.string(),
  sourceHealth: soc2EvidenceInventoryArtifact.shape.sourceHealth,
  freshness: soc2EvidenceInventoryArtifact.shape.freshness,
  retentionState: soc2EvidenceInventoryArtifact.shape.retention.shape.state,
  severity: z.enum(["medium", "high", "critical"]),
  controlIds: z.array(z.string()),
  findingIds: z.array(z.string()),
  observationIds: z.array(z.string()),
  affectedStatus: z.literal("Stale/not tested"),
  reason: z.string(),
  action: z.string(),
  detectedAt: z.string(),
  nextCheckDueAt: z.string(),
});

const soc2SourceAlertReport = z.object({
  schemaVersion: z.literal(1),
  organization: soc2PreflightPackage.shape.organization,
  auditPeriod: soc2PreflightPackage.shape.auditPeriod,
  generatedAt: z.string(),
  policyVersion: z.string(),
  summary: z.object({
    totalAlerts: z.number().int().nonnegative(),
    critical: z.number().int().nonnegative(),
    high: z.number().int().nonnegative(),
    medium: z.number().int().nonnegative(),
    controlsAtRisk: z.number().int().nonnegative(),
    staleOrBlockedSources: z.number().int().nonnegative(),
  }),
  alerts: z.array(soc2SourceAlert),
});
export type EvidenceSoc2SourceAlertReportDto = z.infer<typeof soc2SourceAlertReport>;

const soc2ReviewDecisionBody = z.object({
  controlId: z.string().min(1),
  findingId: z.string().nullable().optional(),
  decision: soc2ReviewDecisionKind,
  actor: z.string().min(1).max(120),
  actorTenantId: z.string().min(1).optional(),
  actorRole: soc2ActorRole.optional(),
  rationale: z.string().min(1).max(2000),
  evidenceRefs: z.array(z.string()).optional(),
});
export type EvidenceSoc2ReviewDecisionBodyDto = z.infer<typeof soc2ReviewDecisionBody>;

const soc2ReviewDecisionResponse = z.object({
  decision: soc2ReviewDecision,
  receipt: soc2VerificationReceipt,
  run: soc2PreflightStoredRun,
});
export type EvidenceSoc2ReviewDecisionResponseDto = z.infer<
  typeof soc2ReviewDecisionResponse
>;

const soc2ReviewQueueQuery = z.object({
  status: soc2PreflightStatus.optional(),
  owner: z.string().optional(),
  auditPeriodId: z.string().optional(),
  actorTenantId: z.string().min(1).optional(),
  actorRole: soc2ActorRole.optional(),
  limit: z.coerce.number().int().positive().max(200).optional(),
});

const soc2ReviewQueueItem = z.object({
  id: z.string(),
  runId: z.string(),
  runLabel: z.string(),
  organizationName: z.string(),
  auditPeriodId: z.string(),
  generatedAt: z.string(),
  ageHours: z.number().nonnegative(),
  priority: z.number().int().nonnegative(),
  status: soc2PreflightStatus,
  severity: soc2Check.shape.severity,
  owner: z.string(),
  controlId: z.string(),
  controlTitle: z.string(),
  controlText: z.string(),
  requirementId: z.string().nullable(),
  checkId: z.string().nullable(),
  reason: z.string(),
  action: z.string(),
  evidenceRefs: z.array(z.string()),
  policyVersion: z.string(),
  receiptId: z.string().nullable(),
  reviewed: z.boolean(),
});
export type EvidenceSoc2ReviewQueueItemDto = z.infer<typeof soc2ReviewQueueItem>;

const soc2ReviewQueueResponse = z.object({
  generatedAt: z.string(),
  summary: z.object({
    totalActionable: z.number().int().nonnegative(),
    shown: z.number().int().nonnegative(),
    exceptions: z.number().int().nonnegative(),
    needsDetermination: z.number().int().nonnegative(),
    staleNotTested: z.number().int().nonnegative(),
    reviewedFiltered: z.number().int().nonnegative(),
  }),
  items: z.array(soc2ReviewQueueItem),
});
export type EvidenceSoc2ReviewQueueDto = z.infer<typeof soc2ReviewQueueResponse>;

const soc2SemanticWorkerStatus = z.enum([
  "ready_to_dispatch",
  "worker_active",
  "stale_lease",
  "retry_needed",
  "result_ready",
  "malformed_result",
  "retry_exhausted",
]);

const soc2SemanticWorkerAction = z.enum([
  "dispatch",
  "wait",
  "requeue",
  "retry",
  "review_result",
  "repair_result",
  "escalate",
]);

const soc2SemanticWorkerSeverity = z.enum(["info", "warning", "blocking"]);

const soc2SemanticWorkerQuery = soc2AccessContextQuery.extend({
  runId: z.string().optional(),
  limit: z.coerce.number().int().positive().max(200).optional(),
});

const soc2SemanticWorkerReviewRow = z.object({
  schemaVersion: z.literal(1),
  dispatchId: z.string(),
  checkResultId: z.string(),
  tenantId: z.string(),
  runId: z.string(),
  controlId: z.string(),
  controlTitle: z.string(),
  requirementId: z.string(),
  status: soc2SemanticWorkerStatus,
  action: soc2SemanticWorkerAction,
  severity: soc2SemanticWorkerSeverity,
  attempt: z.number().int().nonnegative(),
  leaseOwnerId: z.string().nullable(),
  leaseExpiresAt: z.string().nullable(),
  workerId: z.string().nullable(),
  workerStatus: z.enum(["healthy", "starting", "stopped", "failed"]).nullable(),
  workerHealthReason: z.string().nullable(),
  resultJudgmentId: z.string().nullable(),
  reason: z.string(),
});
export type EvidenceSoc2SemanticWorkerReviewRowDto = z.infer<
  typeof soc2SemanticWorkerReviewRow
>;

const soc2SemanticWorkerReviewResponse = z.object({
  schemaVersion: z.literal(1),
  tenantId: z.string(),
  runId: z.string(),
  generatedAt: z.string(),
  source: z.enum(["route-fed-reviewer-preview", "workspace-semantic-worker-queue"]),
  persistedQueueBacked: z.boolean(),
  summary: z.object({
    total: z.number().int().nonnegative(),
    readyToDispatch: z.number().int().nonnegative(),
    active: z.number().int().nonnegative(),
    stale: z.number().int().nonnegative(),
    retryNeeded: z.number().int().nonnegative(),
    resultReady: z.number().int().nonnegative(),
    malformed: z.number().int().nonnegative(),
    retryExhausted: z.number().int().nonnegative(),
    workerBlocked: z.number().int().nonnegative(),
    blocking: z.number().int().nonnegative(),
  }),
  releaseReady: z.boolean(),
  rows: z.array(soc2SemanticWorkerReviewRow),
});
export type EvidenceSoc2SemanticWorkerReviewDto = z.infer<
  typeof soc2SemanticWorkerReviewResponse
>;

const soc2RemediationQuery = z.object({
  state: z
    .enum(["open", "evidence_requested", "investigation_open", "exception_recorded", "accepted", "verified_after_fix"])
    .optional(),
  owner: z.string().optional(),
  auditPeriodId: z.string().optional(),
  actorTenantId: z.string().min(1).optional(),
  actorRole: soc2ActorRole.optional(),
  limit: z.coerce.number().int().positive().max(200).optional(),
});

const soc2RemediationItem = z.object({
  id: z.string(),
  runId: z.string(),
  runLabel: z.string(),
  organizationName: z.string(),
  auditPeriodId: z.string(),
  controlId: z.string(),
  controlTitle: z.string(),
  owner: z.string(),
  state: z.enum(["open", "evidence_requested", "investigation_open", "exception_recorded", "accepted", "verified_after_fix"]),
  status: soc2PreflightStatus,
  severity: soc2Check.shape.severity,
  reason: z.string(),
  action: z.string(),
  dueAt: z.string(),
  requiresReverification: z.boolean(),
  findingId: z.string(),
  decisionId: z.string().nullable(),
  receiptId: z.string().nullable(),
  evidenceRefs: z.array(z.string()),
  policyVersion: z.string(),
  assignment: z.object({
    schemaVersion: z.literal(1),
    assignee: z.string(),
    assignedAt: z.string(),
    dueAt: z.string(),
    source: z.enum(["control_owner", "review_decision"]),
    decisionId: z.string().nullable(),
  }),
  comments: z.array(
    z.object({
      schemaVersion: z.literal(1),
      id: z.string(),
      actor: z.string(),
      body: z.string(),
      commentedAt: z.string(),
      kind: z.enum(["review_note", "evidence_request", "remediation_note", "exception_rationale"]),
      decisionId: z.string(),
    }),
  ),
  escalation: z
    .object({
      schemaVersion: z.literal(1),
      state: z.enum(["evidence_requested", "investigation_open"]),
      reason: z.string(),
      actor: z.string().nullable(),
      escalatedAt: z.string(),
      decisionId: z.string().nullable(),
    })
    .nullable(),
});
export type EvidenceSoc2RemediationItemDto = z.infer<typeof soc2RemediationItem>;

const soc2RemediationResponse = z.object({
  generatedAt: z.string(),
  summary: z.object({
    total: z.number().int().nonnegative(),
    shown: z.number().int().nonnegative(),
    open: z.number().int().nonnegative(),
    evidenceRequested: z.number().int().nonnegative(),
    investigationOpen: z.number().int().nonnegative(),
    exceptionRecorded: z.number().int().nonnegative(),
    accepted: z.number().int().nonnegative(),
    verifiedAfterFix: z.number().int().nonnegative(),
    requiresReverification: z.number().int().nonnegative(),
  }),
  items: z.array(soc2RemediationItem),
});
export type EvidenceSoc2RemediationDto = z.infer<typeof soc2RemediationResponse>;

const soc2ScheduleStatus = z.enum(["on_schedule", "due_soon", "missed", "blocked"]);

const soc2ScheduleQuery = z.object({
  runId: z.string().optional(),
  status: soc2ScheduleStatus.optional(),
  actorTenantId: z.string().min(1).optional(),
  actorRole: soc2ActorRole.optional(),
  limit: z.coerce.number().int().positive().max(200).optional(),
});

const soc2ScheduleItem = z.object({
  schemaVersion: z.literal(1),
  id: z.string(),
  runId: z.string(),
  runLabel: z.string(),
  organizationName: z.string(),
  auditPeriodId: z.string(),
  controlId: z.string(),
  controlTitle: z.string(),
  owner: z.string(),
  frequency: z.string(),
  cadenceDays: z.number().int().positive().nullable(),
  latestEvidenceCollectedAt: z.string().nullable(),
  nextRefreshDueAt: z.string().nullable(),
  missedByDays: z.number().int().nonnegative(),
  status: soc2ScheduleStatus,
  severity: z.enum(["medium", "high", "critical"]),
  affectedStatus: z.literal("Stale/not tested").nullable(),
  evidenceRefs: z.array(z.string()),
  findingIds: z.array(z.string()),
  reason: z.string(),
  action: z.string(),
});

const soc2ScheduleReport = z.object({
  generatedAt: z.string(),
  summary: z.object({
    total: z.number().int().nonnegative(),
    shown: z.number().int().nonnegative(),
    onSchedule: z.number().int().nonnegative(),
    dueSoon: z.number().int().nonnegative(),
    missed: z.number().int().nonnegative(),
    blocked: z.number().int().nonnegative(),
    affectedControls: z.number().int().nonnegative(),
  }),
  items: z.array(soc2ScheduleItem),
});
export type EvidenceSoc2ScheduleReportDto = z.infer<typeof soc2ScheduleReport>;

const soc2NotificationChannel = z.enum(["email", "webhook"]);
const soc2NotificationDeliveryState = z.enum([
  "pending",
  "disabled",
  "not_configured",
]);

const soc2NotificationSettings = z.object({
  schemaVersion: z.literal(1),
  enabled: z.boolean(),
  channels: z.array(soc2NotificationChannel),
  webhookUrl: z.string().nullable(),
  emailRecipients: z.array(z.string()),
  dedupeWindowHours: z.number().int().positive(),
});

const soc2NotificationOutboxQuery = z.object({
  runId: z.string().optional(),
  kind: z.enum(["source_alert", "remediation_due", "missed_schedule"]).optional(),
  actorTenantId: z.string().min(1).optional(),
  actorRole: soc2ActorRole.optional(),
  limit: z.coerce.number().int().positive().max(200).optional(),
});

const soc2NotificationDelivery = z.object({
  channel: soc2NotificationChannel,
  state: soc2NotificationDeliveryState,
  target: z.string().nullable(),
  dedupeKey: z.string(),
});

const soc2NotificationEvent = z.object({
  schemaVersion: z.literal(1),
  id: z.string(),
  dedupeKey: z.string(),
  kind: z.enum(["source_alert", "remediation_due", "missed_schedule"]),
  severity: z.enum(["medium", "high", "critical"]),
  runId: z.string(),
  runLabel: z.string(),
  organizationName: z.string(),
  auditPeriodId: z.string(),
  title: z.string(),
  body: z.string(),
  evidenceRefs: z.array(z.string()),
  findingIds: z.array(z.string()),
  controlIds: z.array(z.string()),
  route: z.string(),
  dueAt: z.string(),
  createdAt: z.string(),
  deliveries: z.array(soc2NotificationDelivery),
});

const soc2NotificationOutbox = z.object({
  generatedAt: z.string(),
  settings: soc2NotificationSettings,
  summary: z.object({
    total: z.number().int().nonnegative(),
    sourceAlerts: z.number().int().nonnegative(),
    remediationDue: z.number().int().nonnegative(),
    missedSchedules: z.number().int().nonnegative(),
    pendingDeliveries: z.number().int().nonnegative(),
    disabledDeliveries: z.number().int().nonnegative(),
    notConfiguredDeliveries: z.number().int().nonnegative(),
    deduped: z.number().int().nonnegative(),
  }),
  events: z.array(soc2NotificationEvent),
});
export type EvidenceSoc2NotificationOutboxDto = z.infer<
  typeof soc2NotificationOutbox
>;

const soc2ObservationCorrectionBody = z.object({
  observationId: z.string().min(1),
  actor: z.string().min(1).max(120),
  actorTenantId: z.string().min(1).optional(),
  actorRole: soc2ActorRole.optional(),
  correctedValue: z.string().min(1).max(2000),
  rationale: z.string().min(1).max(2000),
});
export type EvidenceSoc2ObservationCorrectionBodyDto = z.infer<
  typeof soc2ObservationCorrectionBody
>;

const soc2ObservationCorrectionResponse = z.object({
  correction: soc2ObservationCorrection,
  receipt: soc2VerificationReceipt,
  run: soc2PreflightStoredRun,
});
export type EvidenceSoc2ObservationCorrectionResponseDto = z.infer<
  typeof soc2ObservationCorrectionResponse
>;

const soc2TemplateMappingBody = z.object({
  controlId: z.string().min(1),
  templateId: soc2TemplateId.nullable(),
  actor: z.string().min(1).max(120),
  actorTenantId: z.string().min(1).optional(),
  actorRole: soc2ActorRole.optional(),
  rationale: z.string().min(1).max(2000),
});
export type EvidenceSoc2TemplateMappingBodyDto = z.infer<
  typeof soc2TemplateMappingBody
>;

const soc2TemplateMappingResponse = z.object({
  event: soc2TemplateMappingEvent,
  mapping: soc2ControlTemplateMapping,
  run: soc2PreflightStoredRun,
});
export type EvidenceSoc2TemplateMappingResponseDto = z.infer<
  typeof soc2TemplateMappingResponse
>;

const soc2PreflightSampleOp: Operation<
  void,
  void,
  void,
  EvidenceSoc2PreflightReportDto
> = {
  method: "GET",
  path: () => "/api/evidence/soc2/preflight/sample",
  response: soc2PreflightReport,
};

const soc2PreflightBatchJobsOp: Operation<
  void,
  void,
  void,
  EvidenceSoc2PreflightBatchJobsDto
> = {
  method: "GET",
  path: () => "/api/evidence/soc2/preflight/batch-jobs",
  response: soc2PreflightBatchJobsResponse,
};

const soc2PreflightRunsOp: Operation<
  void,
  z.infer<typeof soc2AccessContextQuery>,
  void,
  EvidenceSoc2PreflightRunsDto
> = {
  method: "GET",
  path: () => "/api/evidence/soc2/preflight/runs",
  query: soc2AccessContextQuery,
  response: soc2PreflightRunsResponse,
};

const soc2PreflightImportOnboardingStateOp: Operation<
  void,
  z.infer<typeof soc2AccessContextQuery>,
  void,
  EvidenceSoc2PreflightImportOnboardingStateDto
> = {
  method: "GET",
  path: () => "/api/evidence/soc2/preflight/import-onboarding-state",
  query: soc2AccessContextQuery,
  response: soc2PreflightImportOnboardingStateResponse,
};

const soc2PreflightPolicyStudioStateOp: Operation<
  void,
  z.infer<typeof soc2AccessContextQuery>,
  void,
  EvidenceSoc2PreflightPolicyStudioStateDto
> = {
  method: "GET",
  path: () => "/api/evidence/soc2/preflight/policy-studio-state",
  query: soc2AccessContextQuery,
  response: soc2PreflightPolicyStudioStateResponse,
};

const soc2PreflightAccessAuditOp: Operation<
  void,
  z.infer<typeof soc2AccessAuditQuery>,
  void,
  EvidenceSoc2AccessAuditDto
> = {
  method: "GET",
  path: () => "/api/evidence/soc2/preflight/access-audit",
  query: soc2AccessAuditQuery,
  response: soc2AccessAuditResponse,
};

const soc2PreflightScorecardOp: Operation<
  { id: string },
  z.infer<typeof soc2AccessContextQuery>,
  void,
  EvidenceSoc2Phase1ScorecardDto
> = {
  method: "GET",
  path: ({ id }) => `/api/evidence/soc2/preflight/runs/${id}/scorecard`,
  params: z.object({ id: z.string().min(1) }),
  query: soc2AccessContextQuery,
  response: soc2Phase1Scorecard,
};

const soc2PreflightEvidenceInventoryOp: Operation<
  { id: string },
  z.infer<typeof soc2AccessContextQuery>,
  void,
  EvidenceSoc2EvidenceInventoryDto
> = {
  method: "GET",
  path: ({ id }) => `/api/evidence/soc2/preflight/runs/${id}/evidence-inventory`,
  params: z.object({ id: z.string().min(1) }),
  query: soc2AccessContextQuery,
  response: soc2EvidenceInventory,
};

const soc2PreflightSourceAlertsOp: Operation<
  { id: string },
  z.infer<typeof soc2AccessContextQuery>,
  void,
  EvidenceSoc2SourceAlertReportDto
> = {
  method: "GET",
  path: ({ id }) => `/api/evidence/soc2/preflight/runs/${id}/source-alerts`,
  params: z.object({ id: z.string().min(1) }),
  query: soc2AccessContextQuery,
  response: soc2SourceAlertReport,
};

const soc2PreflightReviewQueueOp: Operation<
  void,
  z.infer<typeof soc2ReviewQueueQuery>,
  void,
  EvidenceSoc2ReviewQueueDto
> = {
  method: "GET",
  path: () => "/api/evidence/soc2/preflight/review-queue",
  query: soc2ReviewQueueQuery,
  response: soc2ReviewQueueResponse,
};

const soc2PreflightRemediationsOp: Operation<
  void,
  z.infer<typeof soc2RemediationQuery>,
  void,
  EvidenceSoc2RemediationDto
> = {
  method: "GET",
  path: () => "/api/evidence/soc2/preflight/remediations",
  query: soc2RemediationQuery,
  response: soc2RemediationResponse,
};

const soc2PreflightSchedulesOp: Operation<
  void,
  z.infer<typeof soc2ScheduleQuery>,
  void,
  EvidenceSoc2ScheduleReportDto
> = {
  method: "GET",
  path: () => "/api/evidence/soc2/preflight/schedules",
  query: soc2ScheduleQuery,
  response: soc2ScheduleReport,
};

const soc2PreflightNotificationsOp: Operation<
  void,
  z.infer<typeof soc2NotificationOutboxQuery>,
  void,
  EvidenceSoc2NotificationOutboxDto
> = {
  method: "GET",
  path: () => "/api/evidence/soc2/preflight/notifications",
  query: soc2NotificationOutboxQuery,
  response: soc2NotificationOutbox,
};

const soc2PreflightRunOp: Operation<
  void,
  void,
  EvidenceSoc2PreflightRunBodyDto,
  EvidenceSoc2PreflightReportDto
> = {
  method: "POST",
  path: () => "/api/evidence/soc2/preflight/run",
  body: soc2PreflightRunBody,
  response: soc2PreflightReport,
  invalidates: [
    soc2PreflightRunsOp,
    soc2PreflightReviewQueueOp,
    soc2PreflightRemediationsOp,
    soc2PreflightSchedulesOp,
    soc2PreflightNotificationsOp,
  ],
};

const soc2PreflightImportOp: Operation<
  void,
  void,
  EvidenceSoc2PreflightImportBodyDto,
  EvidenceSoc2PreflightImportDto
> = {
  method: "POST",
  path: () => "/api/evidence/soc2/preflight/import",
  body: soc2PreflightImportBody,
  response: soc2PreflightImportResponse,
  invalidates: [
    soc2PreflightRunsOp,
    soc2PreflightReviewQueueOp,
    soc2PreflightImportOnboardingStateOp,
    soc2PreflightPolicyStudioStateOp,
    soc2PreflightAccessAuditOp,
  ],
};

const soc2PreflightBatchRunOp: Operation<
  void,
  void,
  EvidenceSoc2PreflightBatchRunBodyDto,
  EvidenceSoc2PreflightBatchRunDto
> = {
  method: "POST",
  path: () => "/api/evidence/soc2/preflight/batch-run",
  body: soc2PreflightBatchRunBody,
  response: soc2PreflightBatchRunResponse,
  invalidates: [
    soc2PreflightRunsOp,
    soc2PreflightBatchJobsOp,
    soc2PreflightReviewQueueOp,
    soc2PreflightRemediationsOp,
    soc2PreflightSchedulesOp,
    soc2PreflightNotificationsOp,
  ],
};

const soc2PreflightBatchSampleBody = z.object({
  idempotencyKey: z.string().min(1).max(200).optional(),
  simulateFailureAfterControls: z.number().int().nonnegative().optional(),
});
export type EvidenceSoc2PreflightBatchSampleBodyDto = z.infer<
  typeof soc2PreflightBatchSampleBody
>;

const soc2PreflightBatchSampleOp: Operation<
  void,
  void,
  EvidenceSoc2PreflightBatchSampleBodyDto,
  EvidenceSoc2PreflightBatchRunDto
> = {
  method: "POST",
  path: () => "/api/evidence/soc2/preflight/batch-sample",
  body: soc2PreflightBatchSampleBody,
  response: soc2PreflightBatchRunResponse,
  invalidates: [
    soc2PreflightRunsOp,
    soc2PreflightBatchJobsOp,
    soc2PreflightReviewQueueOp,
    soc2PreflightRemediationsOp,
    soc2PreflightSchedulesOp,
    soc2PreflightNotificationsOp,
  ],
};

// invalidation-ignore: write-nothing POST — parses uploaded CSV into a preview only
const soc2PreflightCsvPreviewOp: Operation<
  void,
  void,
  EvidenceSoc2CsvImportBodyDto,
  EvidenceSoc2CsvPreviewDto
> = {
  method: "POST",
  path: () => "/api/evidence/soc2/preflight/csv-preview",
  body: soc2CsvImportBody,
  response: soc2CsvPreviewResponse,
};

const soc2PreflightCsvImportOp: Operation<
  void,
  void,
  EvidenceSoc2CsvImportBodyDto,
  EvidenceSoc2PreflightImportDto
> = {
  method: "POST",
  path: () => "/api/evidence/soc2/preflight/csv-import",
  body: soc2CsvImportBody,
  response: soc2PreflightImportResponse,
  invalidates: [
    soc2PreflightRunsOp,
    soc2PreflightReviewQueueOp,
    soc2PreflightImportOnboardingStateOp,
    soc2PreflightPolicyStudioStateOp,
    soc2PreflightAccessAuditOp,
  ],
};

// invalidation-ignore: write-nothing POST — parses uploaded XLSX into a preview only
const soc2PreflightXlsxPreviewOp: Operation<
  void,
  void,
  EvidenceSoc2XlsxImportBodyDto,
  EvidenceSoc2XlsxPreviewDto
> = {
  method: "POST",
  path: () => "/api/evidence/soc2/preflight/xlsx-preview",
  body: soc2XlsxImportBody,
  response: soc2CsvPreviewResponse,
};

const soc2PreflightXlsxImportOp: Operation<
  void,
  void,
  EvidenceSoc2XlsxImportBodyDto,
  EvidenceSoc2PreflightImportDto
> = {
  method: "POST",
  path: () => "/api/evidence/soc2/preflight/xlsx-import",
  body: soc2XlsxImportBody,
  response: soc2PreflightImportResponse,
  invalidates: [
    soc2PreflightRunsOp,
    soc2PreflightReviewQueueOp,
    soc2PreflightImportOnboardingStateOp,
    soc2PreflightPolicyStudioStateOp,
    soc2PreflightAccessAuditOp,
  ],
};

const soc2PreflightImportSampleOp: Operation<
  void,
  void,
  void,
  EvidenceSoc2PreflightImportDto
> = {
  method: "POST",
  path: () => "/api/evidence/soc2/preflight/import-sample",
  response: soc2PreflightImportResponse,
  invalidates: [
    soc2PreflightRunsOp,
    soc2PreflightReviewQueueOp,
    soc2PreflightImportOnboardingStateOp,
    soc2PreflightPolicyStudioStateOp,
    soc2PreflightAccessAuditOp,
  ],
};

const soc2PreflightGetOp: Operation<
  { id: string },
  z.infer<typeof soc2AccessContextQuery>,
  void,
  EvidenceSoc2PreflightStoredRunDto
> = {
  method: "GET",
  path: ({ id }) => `/api/evidence/soc2/preflight/runs/${id}`,
  params: z.object({ id: z.string().min(1) }),
  query: soc2AccessContextQuery,
  response: soc2PreflightStoredRun,
};

const soc2PreflightExportOp: Operation<
  { id: string },
  z.infer<typeof soc2AuditPreparationExportQuery> & z.infer<typeof soc2AccessContextQuery>,
  void,
  EvidenceSoc2AuditPreparationExportPacketDto
> = {
  method: "GET",
  path: ({ id }) => `/api/evidence/soc2/preflight/runs/${id}/export`,
  params: z.object({ id: z.string().min(1) }),
  query: soc2AuditPreparationExportQuery.merge(soc2AccessContextQuery),
  response: soc2AuditPreparationExportPacket,
};

const soc2PreflightReplayOp: Operation<
  { id: string },
  z.infer<typeof soc2ReplayQuery> & z.infer<typeof soc2AccessContextQuery>,
  void,
  EvidenceSoc2ReplayDto
> = {
  method: "GET",
  path: ({ id }) => `/api/evidence/soc2/preflight/runs/${id}/replay`,
  params: z.object({ id: z.string().min(1) }),
  query: soc2ReplayQuery.merge(soc2AccessContextQuery),
  response: soc2ReplayResponse,
};

const soc2PreflightSemanticWorkerReviewOp: Operation<
  void,
  z.infer<typeof soc2SemanticWorkerQuery>,
  void,
  EvidenceSoc2SemanticWorkerReviewDto
> = {
  method: "GET",
  path: () => "/api/evidence/soc2/preflight/semantic-worker-review",
  query: soc2SemanticWorkerQuery,
  response: soc2SemanticWorkerReviewResponse,
};

const soc2PreflightReviewDecisionOp: Operation<
  { id: string },
  void,
  EvidenceSoc2ReviewDecisionBodyDto,
  EvidenceSoc2ReviewDecisionResponseDto
> = {
  method: "POST",
  path: ({ id }) => `/api/evidence/soc2/preflight/runs/${id}/review-decisions`,
  params: z.object({ id: z.string().min(1) }),
  body: soc2ReviewDecisionBody,
  response: soc2ReviewDecisionResponse,
  invalidates: [
    soc2PreflightRunsOp,
    soc2PreflightReviewQueueOp,
    soc2PreflightRemediationsOp,
    scoped(soc2PreflightScorecardOp, ({ params }) => ({ id: params.id })),
  ],
};

const soc2PreflightObservationCorrectionOp: Operation<
  { id: string },
  void,
  EvidenceSoc2ObservationCorrectionBodyDto,
  EvidenceSoc2ObservationCorrectionResponseDto
> = {
  method: "POST",
  path: ({ id }) => `/api/evidence/soc2/preflight/runs/${id}/observation-corrections`,
  params: z.object({ id: z.string().min(1) }),
  body: soc2ObservationCorrectionBody,
  response: soc2ObservationCorrectionResponse,
  invalidates: [
    soc2PreflightRunsOp,
    soc2PreflightReviewQueueOp,
    soc2PreflightRemediationsOp,
    scoped(soc2PreflightScorecardOp, ({ params }) => ({ id: params.id })),
    scoped(soc2PreflightEvidenceInventoryOp, ({ params }) => ({ id: params.id })),
    scoped(soc2PreflightSourceAlertsOp, ({ params }) => ({ id: params.id })),
  ],
};

const soc2PreflightTemplateMappingOp: Operation<
  { id: string },
  void,
  EvidenceSoc2TemplateMappingBodyDto,
  EvidenceSoc2TemplateMappingResponseDto
> = {
  method: "POST",
  path: ({ id }) => `/api/evidence/soc2/preflight/runs/${id}/template-mapping`,
  params: z.object({ id: z.string().min(1) }),
  body: soc2TemplateMappingBody,
  response: soc2TemplateMappingResponse,
  invalidates: [
    soc2PreflightRunsOp,
    soc2PreflightReviewQueueOp,
    soc2PreflightRemediationsOp,
    scoped(soc2PreflightScorecardOp, ({ params }) => ({ id: params.id })),
    scoped(soc2PreflightEvidenceInventoryOp, ({ params }) => ({ id: params.id })),
  ],
};

// ── Namespace ────────────────────────────────────────────────────────────────

export const evidence = {
  list: listOp,
  templates: templatesOp,
  readiness: readinessOp,
  workflowCandidates: workflowCandidatesOp,
  workflowCandidateSave: workflowCandidateSaveOp,
  workflowTemplateDraft: workflowTemplateDraftOp,
  get: taskDetailOp,
  platformVerdict: platformVerdictOp,
  bundle: bundleGetOp,
  pack: packGetOp,
  verify: verifyOp,
  receipts: receiptsOp,
  claims: claimsOp,
  silentApprove: silentApproveOp,
  receiptBrowse: receiptBrowseOp,
  rejectBrowse: rejectBrowseOp,
  rejectDetail: rejectDetailOp,
  soc2PreflightSample: soc2PreflightSampleOp,
  soc2PreflightRun: soc2PreflightRunOp,
  soc2PreflightImport: soc2PreflightImportOp,
  soc2PreflightBatchRun: soc2PreflightBatchRunOp,
  soc2PreflightBatchSample: soc2PreflightBatchSampleOp,
  soc2PreflightBatchJobs: soc2PreflightBatchJobsOp,
  soc2PreflightCsvPreview: soc2PreflightCsvPreviewOp,
  soc2PreflightCsvImport: soc2PreflightCsvImportOp,
  soc2PreflightXlsxPreview: soc2PreflightXlsxPreviewOp,
  soc2PreflightXlsxImport: soc2PreflightXlsxImportOp,
  soc2PreflightImportSample: soc2PreflightImportSampleOp,
  soc2PreflightRuns: soc2PreflightRunsOp,
  soc2PreflightImportOnboardingState: soc2PreflightImportOnboardingStateOp,
  soc2PreflightPolicyStudioState: soc2PreflightPolicyStudioStateOp,
  soc2PreflightAccessAudit: soc2PreflightAccessAuditOp,
  soc2PreflightGet: soc2PreflightGetOp,
  soc2PreflightExport: soc2PreflightExportOp,
  soc2PreflightReplay: soc2PreflightReplayOp,
  soc2PreflightScorecard: soc2PreflightScorecardOp,
  soc2PreflightEvidenceInventory: soc2PreflightEvidenceInventoryOp,
  soc2PreflightSourceAlerts: soc2PreflightSourceAlertsOp,
  soc2PreflightReviewQueue: soc2PreflightReviewQueueOp,
  soc2PreflightSemanticWorkerReview: soc2PreflightSemanticWorkerReviewOp,
  soc2PreflightRemediations: soc2PreflightRemediationsOp,
  soc2PreflightSchedules: soc2PreflightSchedulesOp,
  soc2PreflightNotifications: soc2PreflightNotificationsOp,
  soc2PreflightReviewDecision: soc2PreflightReviewDecisionOp,
  soc2PreflightObservationCorrection: soc2PreflightObservationCorrectionOp,
  soc2PreflightTemplateMapping: soc2PreflightTemplateMappingOp,
};
